import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { clientLabel, renderEur, renderInt } from './shared';

export interface OverlapEconomicsRow {
  client_code: string;
  owned_users: number;
  suppressed_users: number;
  recipients: number;
  revenue: number | null;
  revenue_per_1k: number | null;
}

export interface OverlapResponse {
  date: string;
  clients: string[];
  matrix: Array<{ a: string; b: string; overlap_users: number }>;
  by_eligible_count: Array<{ eligible_clients: number; users: number }>;
  economics: {
    window_days: number;
    per_client: OverlapEconomicsRow[];
  };
}

interface Props {
  data: OverlapResponse | null;
  loading: boolean;
}

const headCell = {
  backgroundColor: '#e0e0e0 !important',
  color: 'black',
  fontWeight: 'bold',
} as const;

const UnifiedOverlapBlock: React.FC<Props> = ({ data, loading }) => {
  const matrixLookup = useMemo(() => {
    const m = new Map<string, number>();
    for (const cell of data?.matrix ?? []) {
      m.set(`${cell.a}|${cell.b}`, cell.overlap_users);
    }
    return m;
  }, [data]);

  if (loading && !data) return <CircularProgress sx={{ mb: 2 }} />;
  if (!data) return null;

  const clients = data.clients;
  const lookup = (a: string, b: string): number | null => {
    const [x, y] = [a, b].sort();
    return matrixLookup.get(`${x}|${y}`) ?? null;
  };

  return (
    <Paper sx={{ backgroundColor: '#ffffff', p: 2, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Overlap-Ökonomie ({data.date})
      </Typography>
      <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1.5 }}>
        Basis: heutiges Ownership-Ledger (Multi-Eligible-User in den Tagesaudiences). €/1k aus den
        letzten {data.economics.window_days} Tagen SAM-Sends.
      </Typography>

      {clients.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Keine Overlap-Daten für dieses Datum.
        </Typography>
      ) : (
        <Box display="flex" gap={2} flexWrap="wrap">
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', flex: '1 1 340px' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>∩</TableCell>
                  {clients.map((c) => (
                    <TableCell key={c} sx={headCell} align="right">
                      {clientLabel(c)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((rowClient) => (
                  <TableRow key={rowClient}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{clientLabel(rowClient)}</TableCell>
                    {clients.map((colClient) => (
                      <TableCell key={colClient} align="right">
                        {rowClient === colClient ? '—' : renderInt(lookup(rowClient, colClient))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Client', '€/1k', 'Owned', 'Unterdrückt'].map((h) => (
                      <TableCell key={h} sx={headCell} align={h === 'Client' ? 'left' : 'right'}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.economics.per_client.map((row) => (
                    <TableRow key={row.client_code}>
                      <TableCell>{clientLabel(row.client_code)}</TableCell>
                      <TableCell align="right">
                        {row.revenue_per_1k != null ? renderEur(row.revenue_per_1k) : '–'}
                      </TableCell>
                      <TableCell align="right">{renderInt(row.owned_users)}</TableCell>
                      <TableCell align="right">{renderInt(row.suppressed_users)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default UnifiedOverlapBlock;
